<!--
  - SPDX-FileCopyrightText: 2018 Nextcloud GmbH and Nextcloud contributors
  - SPDX-License-Identifier: AGPL-3.0-or-later
-->

<template>
	<AppContent v-if="loading" :aria-label="t('contacts', 'Contacts')">
		<EmptyContent class="empty-content" :name="t('contacts', 'Loading contacts …')">
			<template #icon>
				<IconLoading :size="20" />
			</template>
		</EmptyContent>
	</AppContent>

	<AppContent v-else-if="isEmptyGroup && !isRealGroup" :aria-label="t('contacts', 'Contacts')">
		<EmptyContent class="empty-content" :name="t('contacts', 'There are no contacts yet')">
			<template #icon>
				<IconContact :size="20" />
			</template>
			<template #description>
				<NcButton v-if="!isReadOnlyAddressbook" variant="primary" @click="newContact">
					{{ t('contacts', 'Create contact') }}
				</NcButton>
			</template>
		</EmptyContent>
	</AppContent>

	<AppContent v-else-if="isEmptyGroup && isRealGroup" :aria-label="t('contacts', 'Contacts')">
		<EmptyContent class="empty-content" :name=" t('contacts', 'There are no contacts in this group')">
			<template #icon>
				<IconContact :size="20" />
			</template>
			<template #description>
				<NcButton v-if="contacts.length === 0" variant="primary" @click="addContactsToGroup(selectedGroup)">
					{{ t('contacts', 'Create contacts') }}
				</NcButton>
				<NcButton v-else variant="primary" @click="addContactsToGroup(selectedGroup)">
					{{ t('contacts', 'Add contacts') }}
				</NcButton>
			</template>
		</EmptyContent>
	</AppContent>

	<AppContent
		v-else
		:aria-label="t('contacts', 'Contacts')"
		:show-details="showDetails"
		@update:show-details="hideDetails">
		<!-- contacts list -->
		<template #list>
			<ContactsList
				:list="contactsList"
				:contacts="contacts"
				:search-query="searchQuery"
				:reload-bus="reloadBus" />
		</template>

		<!-- main contacts details -->
		<ContactDetails :contact-key="selectedContact" :reload-bus="reloadBus" />
	</AppContent>
</template>

<script>
import { emit } from '@nextcloud/event-bus'
import {
	NcAppContent as AppContent,
	NcEmptyContent as EmptyContent,
	NcLoadingIcon as IconLoading,
	NcButton,
} from '@nextcloud/vue'
import mitt from 'mitt'
import IconContact from 'vue-material-design-icons/AccountMultipleOutline.vue'
import ContactDetails from '../ContactDetails.vue'
import ContactsList from '../ContactsList.vue'
import RouterMixin from '../../mixins/RouterMixin.js'

export default {
	name: 'ContactsContent',

	components: {
		AppContent,
		NcButton,
		ContactDetails,
		ContactsList,
		EmptyContent,
		IconContact,
		IconLoading,
	},

	mixins: [RouterMixin],

	props: {
		loading: {
			type: Boolean,
			default: true,
		},

		contactsList: {
			type: Array,
			required: true,
		},
	},

	data() {
		return {
			searchQuery: '',
			// communication for ContactListItem and ContactDetails (reload avatar)
			reloadBus: mitt(),
		}
	},

	computed: {
		// store variables
		contacts() {
			return this.$store.getters.getContacts
		},

		groups() {
			return this.$store.getters.getGroups
		},

		/**
		 * Is this a real group ?
		 * Aka not a dynamically generated one like `All contacts`
		 *
		 * @return {boolean}
		 */
		isRealGroup() {
			return this.groups.findIndex((group) => group.name === this.selectedGroup) > -1
		},

		/**
		 * Is the current group empty
		 *
		 * @return {boolean}
		 */
		isEmptyGroup() {
			return this.contactsList.length === 0
		},

		/**
		 * Is the selected address book read only
		 *
		 * @return {boolean}
		 */
		isReadOnlyAddressbook() {
			return !!this.$store.getters.getAddressbooks
				.find((addressbook) => addressbook.id === this.selectedAddressbook)?.readOnly
		},

		showDetails() {
			return !!this.selectedContact
		},
	},

	methods: {
		/**
		 * Forward the addContactsToGroup event to the parent
		 *
		 * @param {string} groupName the group name
		 */
		addContactsToGroup(groupName) {
			emit('contacts:group:append', groupName)
		},

		/**
		 * Forward the newContact event to the parent
		 */
		newContact() {
			this.$emit('new-contact')
		},

		/**
		 * Show the list and deselect contact
		 */
		hideDetails() {
			// Reset the selected contact
			this.$router.push(this.listRoute())
		},
	},
}
</script>

<style lang="scss" scoped>
.empty-content {
	height: 100%;
}
</style>
